import React, { useEffect } from 'react';

import Modal, { Props as ModalProps } from 'react-modal';
import _merge from 'lodash/merge';
import { ELEMENT_MAX_WIDTH, zIndex } from 'styles';

import Tokens from '@nokia-csf-uxr/freeform-design-tokens';

import { getOverlayStyle, applyMaskBlur, removeMaskBlur } from 'components/MaskStyle';
import { ElevationProps, getElevationStyle } from 'components/Elevation';

import DialogContent from './DialogContent';
import DialogTitle from './DialogTitle';
import DialogFooter from './DialogFooter';

const OVERLAY_STYLE = isMaskLight => ({
  zIndex: zIndex.DIALOG,
  backgroundColor: getOverlayStyle({ isMaskLight }).backgroundColor
});

const CONTENT_STYLE = {
  position: 'absolute' as const,
  top: '15vh',
  left: `calc((100vw - ${ELEMENT_MAX_WIDTH}) / 2)`,
  right: `calc((100vw - ${ELEMENT_MAX_WIDTH}) / 2)`,
  bottom: '15vh',
  width: `${ELEMENT_MAX_WIDTH}`,
  border: 'none',
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'flex-start',
  padding: 0,
  overflow: 'hidden' as const,
  WebkitOverflowScrolling: 'touch' as const,
  borderRadius: Tokens.RADIUS.RADIUS_2,
  background: 'transparent',
  outline: 'none'
};

export interface DialogProps extends Partial<Omit<ModalProps, 'appElement'>> {
  /** The content of the Dialog, consider using `DialogContent`, `DialogTitle` and `DialogFooter` */
  children: React.ReactNode;
  /** Object indicating styles to be used for the modal. It has two keys, `overlay` and `content`. See the `Styles` section for more details.  http://reactcommunity.org/react-modal/  */
  style?: {
    overlay?: React.CSSProperties;
    content?: React.CSSProperties;
  };
  /** Set `true`, to render the dialog. */
  isOpen?: boolean;
  /** `ariaHideApp` indicates if the appElement should be hidden. For more information please read http://reactcommunity.org/react-modal/ */
  ariaHideApp?: boolean;
  /** The reference to the app element, used to apply blur filter and aria-hidden attribute for accessibility. `appElement` is required if `ariaHideApp` is set to `false`. */
  appElement?: React.RefObject<HTMLElement>;
  /** If `true`, the mask will be rendered with light variant. This can be overridden by setting `style.content.backgroundColor` */
  isMaskLight?: boolean;
  /** The container's <Elevation /> configuration, see the `<Elevation/>` documentation for more. */
  elevationProps?: Partial<ElevationProps>;
}

/**
 * Dialog is developed using http://reactcommunity.org/react-modal/ library.
 * Please refer to the 3rd party lib documentation for more information. The react-modal library can be configure using `Dialog` props.
 */
const Dialog:React.FC<DialogProps> = (props: DialogProps) => {
  const {
    style,
    children,
    elevationProps,
    isMaskLight,
    appElement,
    isOpen,
    ...modalProps
  } = props;

  useEffect(() => {
    if (appElement && appElement.current) {
      if (isOpen) {
        applyMaskBlur({ ref: appElement, isMaskLight });
      } else {
        removeMaskBlur({ ref: appElement });
      }
    }
    return () => {
      appElement && appElement.current && removeMaskBlur({ ref: appElement });
    };
  }, [isOpen]);

  const resolvedAppElement = appElement && appElement.current ? appElement.current : undefined;

  return (
    <Modal
      style={_merge({}, {
        overlay: OVERLAY_STYLE(isMaskLight),
        content: {
          ...CONTENT_STYLE,
          ...getElevationStyle(elevationProps)
        },
      }, style)}
      appElement={resolvedAppElement}
      isOpen={isOpen}
      bodyOpenClassName="ccfk-modal-open"
      {...modalProps}
    >
      {children}
    </Modal>
  );
};

Dialog.defaultProps = {
  style: {
    overlay: OVERLAY_STYLE(false),
    content: CONTENT_STYLE
  },
  isOpen: false,
  ariaHideApp: false,
  isMaskLight: false,
  elevationProps: {
    elevationIndex: 24
  },
  appElement: undefined
};

Dialog.displayName = 'Dialog';

export {
  Dialog as default,
  DialogContent,
  DialogTitle,
  DialogFooter
};
